// Tutti domain model. Designed multi-instrument/multi-track from the start: guitar is just one
// Track type; bass/drums slot in later without reshaping Project/Arrangement.

export interface ChordSegment {
  chord: string;
  start: number;
  end: number;
}

export interface KeyInfo {
  tonic: string;
  quality: "major" | "minor";
  method?: string;
  confidence?: number;
  refined_by?: string;
  runner_up?: string;
  parent_scale?: string;
  chroma_alt?: string;
  coverage?: number;
}

// Server analysis output — read-only facts about the song.
export interface Analysis {
  duration: number;
  bpm: number;
  key: KeyInfo;
  mode: { mode: string; evidence: string[] };
  scales: string[];
  chords: ChordSegment[];
  beats: number[];
  downbeats: number[];
  chord_histogram?: { chord: string; share: number }[];
  engine?: Record<string, string>;
}

// ~22 guitar techniques (alphaTex effect tokens). bup/bdn are the rhythmic bend-release segments.
export type TechniqueId =
  | "b" | "bh" | "bq" | "br" | "bup" | "bdn"
  | "h" | "sl" | "ss" | "sib" | "sia" | "sou" | "sod"
  | "v" | "vw" | "pm" | "lr" | "st" | "ac" | "x"
  | "nh" | "ah" | "ph" | "tt" | "tp" | "tr";

export type NoteDuration = 1 | 2 | 4 | 8 | 16 | 32;   // whole · half · quarter · 8th · 16th · 32nd

// One materialized event in a measure's editable overlay. Superset across instruments
// (string/fret for fretted; drum voice added later).
export interface NoteEvent {
  rest?: boolean;
  dur: NoteDuration;
  string?: number;
  fret?: number;
  fx?: TechniqueId | TechniqueId[] | null;   // one or more stacked techniques (e.g. bend + vibrato)
  tie?: boolean;
  dotted?: boolean;     // augmentation dot (점음표): +half the duration
  triplet?: boolean;    // 3연음 (triplet member): 3 in the time of 2 → ⅔ duration
  grace?: boolean;      // grace/ornament note (꾸밈음): 0-duration, before-beat
  graceSlide?: boolean; // grace transition: true = slide into target, false/undef = hammer-on
  chord?: boolean;      // stacked double-stop: merges into the previous beat
  ghost?: boolean;      // preview / parenthesized
  chordLabel?: string;  // per-beat chord text
}

// normalize a note's fx (legacy single value OR array OR null) to a plain list of technique ids
export function fxList(fx: TechniqueId | TechniqueId[] | null | undefined): TechniqueId[] {
  return Array.isArray(fx) ? fx : (fx ? [fx] : []);
}

// techniques in the same group are mutually exclusive on one note; different groups stack
export const FX_GROUP: Partial<Record<TechniqueId, string>> = {
  b: "bend", bh: "bend", bq: "bend", br: "bend", bup: "bend", bdn: "bend",
  sl: "slide", ss: "slide", sib: "slide", sia: "slide", sou: "slide", sod: "slide",
  v: "vib", vw: "vib",
  nh: "harm", ah: "harm", ph: "harm",
  h: "play", tr: "play", tt: "play", tp: "play",
};

// toggle a technique within a note's fx list, replacing any same-group member
export function toggleFx(list: TechniqueId[], fx: TechniqueId): TechniqueId[] {
  if (list.includes(fx)) return list.filter((f) => f !== fx);
  const grp = FX_GROUP[fx];
  const base = grp ? list.filter((f) => FX_GROUP[f] !== grp) : list.slice();
  return [...base, fx];
}

// Notation-only overlay, keyed by measure index. NEVER alters startTime/endTime (song-sync invariant).
export interface MeasureEdit {
  chord?: string;
  timeSignature?: [number, number];
  tempo?: number;
  notes?: NoteEvent[];
}

export interface Arrangement {
  measures: Record<number, MeasureEdit>;
}

export type InstrumentType = "guitar" | "bass" | "drums";

export interface Track {
  id: string;
  instrument: InstrumentType;
  name: string;
  tuning: number[];      // MIDI of open strings, high→low. guitar=[64,59,55,50,45,40]
  fretCount?: number;    // 21 for guitar/bass; undefined for drums
  arrangement: Arrangement;
}

export type SongSource =
  | { kind: "default" }
  | { kind: "youtube"; videoId: string }
  | { kind: "localFile"; fileName: string; blobKey?: string };

export interface Project {
  id: string;
  createdAt: number;
  updatedAt: number;
  title: string;
  source: SongSource;
  analysis: Analysis;
  beatLevelMul: 0.5 | 1 | 2;
  tracks: Track[];
  schemaVersion: number;
  // reserved for future cloud sync (accounts) — not used in M1:
  remoteId?: string;
  syncedAt?: number;
}

// ---- derived render data (not persisted) ----
export interface ChartBeat {
  time: number;
  next: number;
  chord: string | null;
  changed: boolean;
}
export interface ChartMeasure {
  beats: ChartBeat[];
  startTime: number;
  endTime: number;
  startTick?: number;
  durTick?: number;
  inserted?: boolean;
}

// raw beat group (notes + chord stacked into one beat)
export interface BeatGroup {
  dur: NoteDuration;
  rest: boolean;
  items: { n: NoteEvent; idx: number }[];
}
